import isPropValid from "@emotion/is-prop-valid";
import styled from "@emotion/styled";
import { Tab, type TabProps, Tabs, type TabsProps } from "@mui/material";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Children, cloneElement, isValidElement } from "react";

type TabFilterItemProps = TabProps & {
  readonly $last?: boolean;
  readonly $first?: boolean;
};

const StyledTabs = styled(Tabs)`
  height: 40px;
  marginBottom: ${(props) => props.theme.spacing(Spacing.XL)};
  marginTop: ${(props) => props.theme.spacing(Spacing.M)};
  
  .MuiTabs-flexContainer {
    display: inline-block;
    text-transform: none;
  }

  .Mui-selected:last-child {
    border-top-right-radius: 8px;
    border-bottom-right-radius: 8px;
  }

  .Mui-selected {
    color: ${(props) => props.theme.palette.text.primary} !important;
    background-color: ${(props) => props.theme.colors.backgroundSecondary} !important;
  }
`;

const StyledTab = styled(Tab)<TabFilterItemProps>`
  text-transform: none;
  border-top: 1px solid ${(props) => props.theme.colors.border};
  border-bottom: 1px solid ${(props) => props.theme.colors.border};
  border-right: 1px solid ${(props) => props.theme.colors.border};
  border-left: ${(props) => (props.$first ? `1px solid ${props.theme.colors.border}` : "unset")};
  height: 40px;
  min-height: unset;

  border-top-right-radius: ${(props) => (props.$last ? "8px" : "0")};
  border-bottom-right-radius: ${(props) => (props.$last ? "8px" : "0")};
  border-top-left-radius: ${(props) => (props.$first ? "8px" : "0")};
  border-bottom-left-radius: ${(props) => (props.$first ? "8px" : "0")};

  margin-right: ${(props) => (props.$last ? props.theme.spacing(Spacing.L) : "0")};
`;

const TabFilterComponent: React.FC<TabsProps> = ({ value, onChange, children, ...props }) => {
  return (
    <StyledTabs
      value={value}
      onChange={onChange}
      TabIndicatorProps={{
        style: {
          display: "none",
        },
      }}
      {...props}
    >
      {Children.map(children, (child, index) => {
        if (isValidElement(child)) {
          if (index === 0) {
            return cloneElement(child as React.ReactElement<TabFilterItemProps>, { $first: true });
          }
          if (index === Children.count(children) - 1) {
            return cloneElement(child as React.ReactElement<TabFilterItemProps>, { $last: true });
          }
        }
        return child;
      })}
    </StyledTabs>
  );
};

const TabFilterItemComponent: React.FC<TabFilterItemProps> = (props) => <StyledTab {...props} />;

export const TabFilter = Object.assign(TabFilterComponent, {
  Item: TabFilterItemComponent,
});
